import React from "react";
import { GridColDef } from "@mui/x-data-grid";
import { useVideoPlatformStore } from "../../store/videoPlatformStore";
import { Box, IconButton, Tooltip } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import PeopleIcon from "@mui/icons-material/People";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ChatIcon from "@mui/icons-material/Chat";
import { FilterableDataGrid, FilterConfig } from "../common/FilterableDataGrid";
import { MeetingAttendeesDialog } from "./MeetingAttendeesDialog";
import { MeetingChatDialog } from "./MeetingChatDialog";
import { getStatusColor, formatDateTime } from "./utils";
import { useSnackbar } from "../../hooks/useSnackbar";

interface VideoPlatformListProps {
  onView: (meetingId: string) => void;
  onDelete: (meetingId: string) => void;
}

export const VideoPlatformList: React.FC<VideoPlatformListProps> = ({
  onView,
  onDelete,
}) => {
  const { meetings } = useVideoPlatformStore();
  const [attendeesDialogOpen, setAttendeesDialogOpen] = React.useState(false);
  const [selectedMeetingForAttendees, setSelectedMeetingForAttendees] =
    React.useState<{
      id: string;
      title: string;
    } | null>(null);
  const [chatDialogOpen, setChatDialogOpen] = React.useState(false);
  const [selectedMeetingForChat, setSelectedMeetingForChat] = React.useState<{
    id: string;
    title: string;
  } | null>(null);
  const { showSnackbar, SnackbarNode } = useSnackbar();

  const handleViewAttendees = (meetingId: string) => {
    const meeting = meetings.find((m) => m.id === meetingId);
    if (meeting) {
      setSelectedMeetingForAttendees({
        id: meeting.externalMeetingId || meeting.id,
        title: meeting.title,
      });
      setAttendeesDialogOpen(true);
    }
  };

  const handleCloseAttendeesDialog = () => {
    setAttendeesDialogOpen(false);
    setSelectedMeetingForAttendees(null);
  };

  const handleViewChat = (meetingId: string) => {
    const meeting = meetings.find((m) => m.id === meetingId);
    if (meeting) {
      setSelectedMeetingForChat({
        id: meeting.externalMeetingId || meeting.id,
        title: meeting.title,
      });
      setChatDialogOpen(true);
    }
  };

  const handleCloseChatDialog = () => {
    setChatDialogOpen(false);
    setSelectedMeetingForChat(null);
  };

  const handleCopyMeeting = (meetingId: string) => {
    const meeting = meetings.find((m) => m.id === meetingId);
    if (meeting) {
      navigator.clipboard.writeText(JSON.stringify(meeting, null, 2));
      showSnackbar("Meeting JSON copied to clipboard!");
    }
  };

  const columns: GridColDef[] = [
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => {
        const colors = getStatusColor(params.value as string);
        return (
          <Box
            component="span"
            sx={{
              backgroundColor: colors.bgcolor,
              color: colors.color,
              padding: "4px 12px",
              borderRadius: "16px",
              fontSize: "0.8125rem",
              display: "inline-flex",
              fontWeight: 500,
            }}
          >
            {params.value}
          </Box>
        );
      },
    },
    {
      field: "type",
      headerName: "Type",
      width: 130,
    },
    {
      field: "createdAt",
      headerName: "Created At",
      width: 200,
      renderCell: (params) => formatDateTime(params.value),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 250,
      sortable: false,
      renderCell: (params) => {
        const isEnded = params.row.status?.toUpperCase() === "ENDED";
        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="View Details">
              <IconButton
                size="small"
                onClick={() => onView(params.row.id)}
                color="primary"
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="View Attendees">
              <IconButton
                size="small"
                onClick={() => handleViewAttendees(params.row.id)}
                color="info"
              >
                <PeopleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="View Chat">
              <IconButton
                size="small"
                onClick={() => handleViewChat(params.row.id)}
                color="secondary"
              >
                <ChatIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Copy JSON">
              <IconButton
                size="small"
                onClick={() => handleCopyMeeting(params.row.id)}
                color="default"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {!isEnded && (
              <Tooltip title="End Meeting">
                <IconButton
                  size="small"
                  onClick={() => onDelete(params.row.id)}
                  color="error"
                >
                  <StopCircleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  const filters: FilterConfig[] = [
    {
      label: "Status",
      field: "status",
      options: [
        { value: "ALL", label: "All Statuses" },
        { value: "ACTIVE", label: "Active" },
        { value: "ENDED", label: "Ended" },
        { value: "CREATED", label: "Created" },
        { value: "CANCELLED", label: "Cancelled" },
      ],
    },
    {
      label: "Type",
      field: "type",
      options: [
        { value: "ALL", label: "All Types" },
        { value: "Instant", label: "Instant" },
        { value: "Scheduled", label: "Scheduled" },
        { value: "Persistent", label: "Persistent" },
      ],
    },
  ];

  return (
    <>
      <FilterableDataGrid
        rows={meetings}
        columns={columns}
        filters={filters}
        searchFields={["title", "id", "externalMeetingId"]}
        searchPlaceholder="Search by title or ID"
        height={600}
        pageSize={10}
      />

      <MeetingAttendeesDialog
        open={attendeesDialogOpen}
        meetingId={selectedMeetingForAttendees?.id || null}
        meetingTitle={selectedMeetingForAttendees?.title || ""}
        onClose={handleCloseAttendeesDialog}
      />

      <MeetingChatDialog
        open={chatDialogOpen}
        meetingId={selectedMeetingForChat?.id || null}
        meetingTitle={selectedMeetingForChat?.title || ""}
        onClose={handleCloseChatDialog}
      />

      {SnackbarNode}
    </>
  );
};
